import { Typography, Modal, useModal, Button, ModalProps } from "matsuri-ui"
import { css } from "@emotion/react"
import { ComponentProps, useMemo } from "react"
import {
    M2MDoorbellChimeIcon,
    M2MKeyVerticalIcon,
    M2MKeyboardAltIcon,
    M2MLocalPostOfficeIcon,
    M2MPasskeyIcon
} from "m2m-icons"
import { UnlockTTLock } from "./UnlockTTLock"
import LargeIconButton from "@/components/LargeIconButton"
import { useTTLockKeyUnlock } from "@/hooks/useTTLockKeyUnlock"
import { RoomKeyInformation } from "@/model/bigQueryInfo"
import { useTranslation } from "@/hooks/useTranslation"
import { theme } from "@/theme"
import {
    LatestSpareKeyboxNumberResponse,
    useLatestSpareKeyboxInfo
} from "@/hooks/useSpareKeyboxRotation"
import {
    SPARE_KEY_NUMBER_UNREGISTERED,
    SPARE_KEY_ROTATION_CONTACT_ADMIN
} from "@/model/spareKeybox"
import { useLatestGuestKeyboxNumber } from "@/hooks/useLatestGuestKeyboxNumber"
import {
    GUEST_KEY_NUMBER_UNREGISTERED,
    resolveGuestKeyHistories,
    resolveGuestKeyNumber
} from "@/model/guestKeyboxNumber"
import { KeyboxNumberHistoryList } from "@/parts/KeyboxNumberHistoryList"

const SpareKeyInfo = ({ data }: { data: LatestSpareKeyboxNumberResponse }) => {
    const { t } = useTranslation()
    return (
        <div
            css={css`
                display: flex;
                flex-direction: column;
                gap: 16px;
            `}
        >
            <div
                css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                `}
            >
                <Typography bolder variant="body">
                    {t("設置方法")}
                </Typography>
                <Typography>{data.installationMethod}</Typography>
            </div>
            <div
                css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                `}
            >
                <Typography bolder variant="body">
                    {t("設置場所")}
                </Typography>
                <Typography>{data.location}</Typography>
            </div>
            <div
                css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                `}
            >
                <Typography bolder variant="body">
                    {t("番号")}
                </Typography>
                {data.isRegistered === false ? (
                    <>
                        <Typography>
                            {t(SPARE_KEY_NUMBER_UNREGISTERED)}
                        </Typography>
                        <Typography variant="caption">
                            {t(SPARE_KEY_ROTATION_CONTACT_ADMIN)}
                        </Typography>
                    </>
                ) : (
                    <Typography>{data.keyboxNumber}</Typography>
                )}
            </div>
            <div
                css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                `}
            >
                <Typography bolder variant="body">
                    {t("外部番号")}
                </Typography>
                <Typography>{data.outLocation}</Typography>
            </div>

            <KeyboxNumberHistoryList
                title="スペアキー変更履歴"
                histories={(data.histories ?? []).map(history => ({
                    date: history.rotationDate,
                    keyboxNumber: history.keyboxNumber
                }))}
            />
        </div>
    )
}

interface RoomKeyDetailsProps {
    operationId?: string
    listingId?: string
    cleaningDateString?: string
    roomKeyInformation?: RoomKeyInformation
    isShowSpareKey?: boolean
    /**
     * 占有部（部屋）の鍵情報を開示してよいかどうか。
     * 清掃の開始打刻前に鍵番号が見えてしまうと打刻せずに作業を始められるため、
     * 開始打刻前は TTLock・ゲストキー・スペアキーを開けないようにする。
     * 共用部の到達に必要なオートロック・ポストは対象外。
     * 未指定の場合は従来どおり開示する（ツアー側は対象外のため）。
     */
    isKeyRevealed?: boolean
    /**
     * ゲストキーの番号を m2m-checkin の最新番号で表示する清掃（またはツアー）の ID。
     * 未指定（共用部のツアーなど）の場合は物件運営情報(BigQuery)の値を表示する。
     */
    guestKeyboxCleaningId?: string
}

const KeyInfoModal = ({
    keyName,
    items,
    afterItems,
    children,
    ...props
}: {
    keyName: string
    items?: { label: string; value: string }[]
    /** items の下に表示する要素（変更履歴など） */
    afterItems?: React.ReactElement
    children?: React.ReactElement
}) => {
    const { t } = useTranslation()
    return (
        <Modal
            width={"calc(100% - 32px)"}
            header={<Typography variant="h3">{t(keyName)}</Typography>}
            body={
                <div>
                    {children ?? (
                        <div
                            css={css`
                                display: flex;
                                flex-direction: column;
                                gap: 16px;
                            `}
                        >
                            {items?.map(item => (
                                <div
                                    key={item.label}
                                    css={css`
                                        display: flex;
                                        flex-direction: column;
                                        gap: 8px;
                                    `}
                                >
                                    <Typography bolder variant="body">
                                        {t(item.label)}
                                    </Typography>
                                    <Typography>{item.value}</Typography>
                                </div>
                            ))}
                            {afterItems}
                        </div>
                    )}
                </div>
            }
            footer={
                <Button
                    fullWidth
                    variant="filled"
                    color="default"
                    onClick={() => {
                        ;(props as { onClose: () => void }).onClose()
                    }}
                >
                    OK
                </Button>
            }
            {...props}
        />
    )
}

interface UnlockTTLockProps extends ModalProps {
    unlockTTLockProps: Omit<ComponentProps<typeof UnlockTTLock>, "onClose">
}

const TTLockUnlockInfoModal = ({
    unlockTTLockProps,
    ...props
}: UnlockTTLockProps) => {
    return (
        <Modal
            width={"calc(100% - 64px)"}
            {...props}
            body={
                <UnlockTTLock
                    {...unlockTTLockProps}
                    onClose={() => props.onClose?.()}
                />
            }
        />
    )
}

export const RoomKeyDetails = ({
    operationId,
    listingId,
    cleaningDateString,
    roomKeyInformation,
    isKeyRevealed = true,
    guestKeyboxCleaningId
}: RoomKeyDetailsProps) => {
    const { t } = useTranslation()
    const spareKeyboxInfo = useLatestSpareKeyboxInfo(operationId)
    const latestGuestKeyboxNumber = useLatestGuestKeyboxNumber(
        guestKeyboxCleaningId
    )

    const autoLockInfo = useMemo(() => {
        if (roomKeyInformation) {
            return {
                hasAutoLock: roomKeyInformation.al ?? "-",
                unlockMethod: roomKeyInformation.al_opening ?? "-",
                unlockNumber: roomKeyInformation.al_unlock_number ?? "-"
            }
        }

        return {
            hasAutoLock: "-",
            unlockMethod: "-",
            unlockNumber: "-"
        }
    }, [roomKeyInformation])

    const postInfo = useMemo(() => {
        if (roomKeyInformation) {
            return {
                hasAutoLock: roomKeyInformation.post_al ?? "-",
                unlockMethod: roomKeyInformation.post_opening ?? "-",
                unlockNumber: roomKeyInformation.post_unlock_number ?? "-"
            }
        }
        return {
            hasAutoLock: "-",
            unlockMethod: "-",
            unlockNumber: "-"
        }
    }, [roomKeyInformation])

    const guestKeyInfo = useMemo(() => {
        const number = resolveGuestKeyNumber(
            roomKeyInformation?.guest_key_number,
            latestGuestKeyboxNumber
        )
        return {
            installationMethod:
                roomKeyInformation?.guest_key_Installation_method ?? "-",
            installationLocation: roomKeyInformation?.guest_key_location ?? "-",
            number:
                number === GUEST_KEY_NUMBER_UNREGISTERED ? t(number) : number,
            externalLocation: roomKeyInformation?.guest_key_out_location ?? "-"
        }
    }, [roomKeyInformation, latestGuestKeyboxNumber, t])

    const guestKeyHistories = useMemo(
        () => resolveGuestKeyHistories(latestGuestKeyboxNumber),
        [latestGuestKeyboxNumber]
    )

    const [openModal] = useModal(KeyInfoModal)

    const { availableTTLock, keyNumber } = useTTLockKeyUnlock({
        cleaningId: operationId,
        listingId
    })
    const [openTTLockModal] = useModal(TTLockUnlockInfoModal)

    const hasGuestKeyInfo = useMemo(
        () =>
            guestKeyInfo.installationMethod !== "-" ||
            guestKeyInfo.installationLocation !== "-" ||
            guestKeyInfo.number !== "-" ||
            guestKeyInfo.externalLocation !== "-",
        [guestKeyInfo]
    )

    const hasSpareKeyInfo = useMemo(
        () =>
            spareKeyboxInfo.installationMethod !== "-" ||
            spareKeyboxInfo.location !== "-" ||
            spareKeyboxInfo.keyboxNumber !== "-" ||
            spareKeyboxInfo.outLocation !== "-",
        [spareKeyboxInfo]
    )

    // マスクを解除したときに実際に開けるようになる鍵があるかどうか。
    // 元々データがない物件で「開始すると鍵番号が表示されます」と案内してしまうと、
    // 開始しても非活性のままで不具合に見えるため、注意書きの表示条件に使う。
    const hasMaskedKeyInfo =
        availableTTLock || hasGuestKeyInfo || hasSpareKeyInfo

    return (
        <>
            <div
                css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                `}
            >
                <Typography variant="h4" bolder>
                    {t("鍵情報")}
                </Typography>

                <div
                    css={css`
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 16px;
                    `}
                >
                    <LargeIconButton
                        onClick={() => {
                            if (!isKeyRevealed) {
                                return
                            }
                            openTTLockModal({
                                unlockTTLockProps: {
                                    listingId,
                                    cleaningId: operationId,
                                    cleaningDateString
                                }
                            })
                        }}
                        text="TTLock"
                        icon={<M2MDoorbellChimeIcon size={48} />}
                        disabled={!availableTTLock || !isKeyRevealed}
                    />
                    <LargeIconButton
                        onClick={() => {
                            if (
                                autoLockInfo.hasAutoLock !== "-" ||
                                autoLockInfo.unlockMethod !== "-" ||
                                autoLockInfo.unlockNumber !== "-"
                            ) {
                                openModal({
                                    keyName: "オートロック情報",
                                    items: [
                                        {
                                            label: "有無",
                                            value: autoLockInfo.hasAutoLock
                                        },
                                        {
                                            label: "解錠方法",
                                            value: autoLockInfo.unlockMethod
                                        },
                                        {
                                            label: "解錠番号",
                                            value: autoLockInfo.unlockNumber
                                        }
                                    ]
                                })
                            }
                        }}
                        text={t("オートロック")}
                        icon={<M2MKeyboardAltIcon size={48} />}
                        disabled={
                            autoLockInfo.hasAutoLock === "-" &&
                            autoLockInfo.unlockMethod === "-" &&
                            autoLockInfo.unlockNumber === "-"
                        }
                    />
                    <LargeIconButton
                        onClick={() => {
                            if (
                                postInfo.hasAutoLock !== "-" ||
                                postInfo.unlockMethod !== "-" ||
                                postInfo.unlockNumber !== "-"
                            ) {
                                openModal({
                                    keyName: "ポスト情報",
                                    items: [
                                        {
                                            label: "有無",
                                            value: postInfo.hasAutoLock
                                        },
                                        {
                                            label: "解錠方法",
                                            value: postInfo.unlockMethod
                                        },
                                        {
                                            label: "解錠番号",
                                            value: postInfo.unlockNumber
                                        }
                                    ]
                                })
                            }
                        }}
                        text={t("ポスト")}
                        icon={<M2MLocalPostOfficeIcon size={48} />}
                        disabled={
                            postInfo.hasAutoLock === "-" &&
                            postInfo.unlockMethod === "-" &&
                            postInfo.unlockNumber === "-"
                        }
                    />
                    <LargeIconButton
                        onClick={() => {
                            if (isKeyRevealed && hasGuestKeyInfo) {
                                openModal({
                                    keyName: "ゲストキー情報",
                                    afterItems: (
                                        <KeyboxNumberHistoryList
                                            title="ゲストキー変更履歴"
                                            histories={guestKeyHistories}
                                        />
                                    ),
                                    items: [
                                        {
                                            label: "設置方法",
                                            value: guestKeyInfo.installationMethod
                                        },
                                        {
                                            label: "設置場所",
                                            value: guestKeyInfo.installationLocation
                                        },
                                        {
                                            label: "番号",
                                            value: guestKeyInfo.number
                                        },
                                        {
                                            label: "外部場所",
                                            value: guestKeyInfo.externalLocation
                                        }
                                    ]
                                })
                            }
                        }}
                        text={t("ゲストキー")}
                        icon={<M2MPasskeyIcon size={48} />}
                        disabled={!isKeyRevealed || !hasGuestKeyInfo}
                    />
                    {
                        <LargeIconButton
                            onClick={() => {
                                if (isKeyRevealed && hasSpareKeyInfo) {
                                    openModal({
                                        keyName: "スペアキー情報",
                                        children: (
                                            <SpareKeyInfo
                                                data={spareKeyboxInfo}
                                            />
                                        )
                                    })
                                }
                            }}
                            text={t("スペアキー")}
                            icon={<M2MKeyVerticalIcon size={48} />}
                            disabled={!isKeyRevealed || !hasSpareKeyInfo}
                        />
                    }
                </div>

                {!isKeyRevealed && hasMaskedKeyInfo && (
                    <Typography
                        variant="caption"
                        css={css`
                            color: ${theme.palette.text.secondary};
                        `}
                    >
                        {t(
                            "TTLock・ゲストキー・スペアキーは、「開始」ボタンを押すと確認できます"
                        )}
                    </Typography>
                )}
            </div>
        </>
    )
}
